'use client';

import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { GroqImage } from '@/helpers/Image';
import { PageManifestoSchema } from '@/sanity/pages/PageManifestoTemplate';

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function LetterSection({ data }: { data: PageManifestoSchema }) {
  const hasContent =
    data?.letterGreeting ||
    data?.letterContent ||
    data?.letterQuote ||
    data?.letterClosing;
  if (!hasContent) return null;

  return (
    <section className="relative w-full flex flex-col items-center pb-[7rem] lg:pb-[8rem]">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.06 }}
        transition={{ duration: 0.9, ease }}
        className="relative w-[min(94.5rem,calc(100%-4rem))] mx-auto bg-[#f8f8f8] rounded-[0.25rem] shadow-[inset_0px_4px_46.9px_0px_rgba(0,0,0,0.12)] px-[3rem] pt-[4rem] pb-[4rem] lg:px-[13.25rem] lg:pt-[8.5rem] lg:pb-[14rem]"
      >
        {data?.letterGreeting && (
          <p className="mb-0 text-[3.5rem] font-medium text-black tracking-[-0.175rem] leading-[1.15] lg:text-[4.5rem] lg:tracking-[-0.225rem]">
            {data.letterGreeting}
          </p>
        )}

        {data?.letterContent && (
          <div className="richtext richtext-start flex flex-col gap-[2.5rem] mt-[3.5rem] prose-headings:text-[2.25rem] prose-headings:tracking-[-0.125rem] lg:gap-[3rem] lg:mt-[4.5rem] lg:prose-headings:text-[2.5rem]">
            <PortableText
              value={data.letterContent}
              components={richTextComponent}
            />
          </div>
        )}

        {data?.letterQuote && (
          <div className="flex flex-col gap-[2.5rem] mt-[2.5rem] lg:gap-[3rem] lg:mt-[3rem]">
            <div className="w-full h-px bg-black/10" />
            <div className="richtext richtext-start flex flex-col gap-[2.5rem] prose-headings:text-[2.125rem] prose-em:text-[2.25rem] prose-em:font-medium prose-em:leading-[1.3] prose-headings:tracking-[-0.125rem] lg:gap-[3rem] lg:prose-headings:text-[2.375rem] prose-em:!text-black lg:prose-em:text-[2.5rem]">
              <PortableText
                value={data.letterQuote}
                components={richTextComponent}
              />
            </div>
          </div>
        )}

        {data?.letterClosing && (
          <div className="richtext richtext-start flex flex-col gap-[2.5rem] mt-[2.5rem] lg:gap-[3rem] lg:mt-[3rem]">
            <PortableText
              value={data.letterClosing}
              components={richTextComponent}
            />
          </div>
        )}

        {/* Signature + role */}
        <div className="flex flex-col gap-[1rem] mt-[4rem]">
          {data?.letterSignature && (
            <div className="w-[20rem] h-[8rem] lg:w-[26rem] lg:h-[10.75rem]">
              <GroqImage
                src={data.letterSignature}
                alt="Signature"
                className="!w-full !h-full !object-contain !object-left"
              />
            </div>
          )}
          {data?.heroAuthorRole && (
            <p className="mb-0 font-geist-mono text-[1.5rem] text-[#228e71] tracking-[-0.053rem] lg:text-[1.75rem]">
              {data.heroAuthorRole}
            </p>
          )}
        </div>

        {/* Founder portrait — hidden on mobile, visible on desktop */}
        {data?.letterFounderPhoto && (
          <div className="absolute lg:bottom-[8.96rem] bottom-[3rem] right-[0rem] lg:right-[13.25rem] w-[23.125rem] h-[23.125rem]">
            <div className="relative w-full h-full">
              <GroqImage
                src={data.letterFounderPhoto}
                alt={data?.heroAuthorName || 'Founder'}
                className="!w-full !h-full !object-cover"
              />
            </div>
          </div>
        )}
      </motion.div>
    </section>
  );
}
